import React, { useEffect } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import type { TutorialStep } from './tutorialConfig';
import './TutorialCoach.css';

interface TutorialCoachProps {
  active: boolean;
  step: number;
  stepData: TutorialStep | null;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

/**
 * Interface de dialogue du tutoriel : un « coach » qui explique et pointe la
 * vraie UI. Deux modes selon l'étape :
 *  - bloquant  : carte centrée avec fond assombri (intro, info, final).
 *  - flottant  : bulle non bloquante en bas d'écran, le joueur continue de jouer
 *                pendant que le coach l'attend (étapes d'action réelle).
 */
const TutorialCoach: React.FC<TutorialCoachProps> = ({
  active,
  step,
  stepData,
  totalSteps,
  isFirstStep,
  isLastStep,
  onNext,
  onBack,
  onSkip,
}) => {
  const { t } = usePageTranslation('game', 'GuidedTutorial');

  // Spotlight : met en surbrillance l'élément DOM ciblé par l'étape.
  useEffect(() => {
    if (!active || !stepData?.anchor) return undefined;
    const el = document.getElementById(stepData.anchor);
    if (!el) return undefined;
    el.classList.add('tutorial-highlight');
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return () => el.classList.remove('tutorial-highlight');
  }, [active, stepData?.anchor]);

  if (!active || !stepData) return null;

  const title = t(`steps.${stepData.id}.title`);
  const description = t(`steps.${stepData.id}.description`);
  const stepIndicator =
    t('stepIndicator')
      ?.replace('{{current}}', String(step + 1))
      ?.replace('{{total}}', String(totalSteps)) || `${step + 1} / ${totalSteps}`;

  const isWaiting = stepData.gate !== 'next';
  const waitingHint = isWaiting ? t(`waiting.${stepData.gate}`) : '';

  const mascot = (
    <div className="tutorial-mascot flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-2xl">
      🧑‍🚒
    </div>
  );

  const progressBar = (
    <div className="flex gap-1.5">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={index}
          className={`h-1.5 flex-1 rounded-full transition-all ${
            index === step ? 'bg-emerald-400' : index < step ? 'bg-emerald-600/70' : 'bg-slate-600/70'
          }`}
        />
      ))}
    </div>
  );

  const skipButton = (
    <button
      type="button"
      onClick={onSkip}
      className="text-xs font-medium text-slate-400 transition-colors hover:text-slate-200"
    >
      {t('skip')}
    </button>
  );

  // ── Étape d'action : coach flottant non bloquant ──
  if (!stepData.blocking) {
    return (
      <div className="pointer-events-none fixed inset-0 z-[1550]">
        <div className="pointer-events-auto absolute inset-x-0 bottom-4 mx-auto flex w-full max-w-md flex-col gap-3 px-4 sm:bottom-6">
          <div className="tutorial-coach-enter rounded-2xl border-2 border-emerald-500/60 bg-slate-900/95 p-4 shadow-2xl shadow-emerald-900/40 backdrop-blur-md">
            <div className="flex items-start gap-3">
              {mascot}
              <div className="min-w-0 flex-1">
                <div className="mb-0.5 flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-400">
                    {t('coachName')} · {stepIndicator}
                  </p>
                  {skipButton}
                </div>
                <h4 className="text-base font-bold leading-tight text-white">{title}</h4>
                <p className="mt-1 whitespace-pre-line text-sm leading-snug text-slate-300">{description}</p>
              </div>
            </div>

            {isWaiting && (
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                <span className="tutorial-waiting-dot text-base leading-none">●</span>
                <span>{waitingHint}</span>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              {!isFirstStep && (
                <button
                  type="button"
                  onClick={onBack}
                  className="rounded-lg border border-slate-600/60 bg-slate-800/70 px-3 py-1.5 text-xs font-semibold text-slate-200 transition hover:bg-slate-700/80"
                >
                  {t('previous')}
                </button>
              )}
              <div className="flex-1" />
              {stepData.gate === 'next' ? (
                <button
                  type="button"
                  onClick={onNext}
                  className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white transition hover:bg-emerald-500"
                >
                  {isLastStep ? t('finish') : t('next')}
                </button>
              ) : (
                stepData.allowManualComplete && (
                  <button
                    type="button"
                    onClick={onNext}
                    className="rounded-lg border border-emerald-500/50 bg-emerald-600/25 px-4 py-1.5 text-xs font-bold text-emerald-100 transition hover:bg-emerald-600/40"
                  >
                    {isLastStep ? t('finish') : t('continueAnyway')}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Étape bloquante : carte centrée avec fond assombri ──
  return (
    <div className="fixed inset-0 z-[1550] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="tutorial-coach-enter relative w-full max-w-lg overflow-hidden rounded-2xl border-2 border-emerald-500/60 bg-slate-900 shadow-2xl">
        <div className="flex items-center gap-3 border-b border-slate-700/60 bg-gradient-to-r from-emerald-700/40 to-slate-800/40 px-5 py-4">
          {mascot}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-300">{t('coachName')}</p>
            <p className="text-xs text-slate-400">{stepIndicator}</p>
          </div>
          {skipButton}
        </div>

        <div className="px-6 py-5">
          <h3 className="mb-2 text-2xl font-bold text-white">{title}</h3>
          <p className="mb-5 whitespace-pre-line leading-relaxed text-slate-300">{description}</p>
          <div className="mb-5">{progressBar}</div>

          <div className="flex items-center gap-3">
            {!isFirstStep && (
              <button
                type="button"
                onClick={onBack}
                className="flex-1 rounded-lg border border-slate-600/60 bg-slate-800/70 px-4 py-2.5 font-semibold text-slate-200 transition hover:bg-slate-700/80"
              >
                {t('previous')}
              </button>
            )}
            <button
              type="button"
              onClick={onNext}
              className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 font-bold text-white transition hover:bg-emerald-500"
            >
              {isLastStep ? t('finish') : t('next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialCoach;
